import { ZARZOUR_UNIFIED_DARK } from "./zarzourTheme";

export type AppearancePresetId =
  | "signature-dark"
  | "midnight"
  | "graphite"
  | "amoled"
  | "daylight"
  | "custom";

export type AppearancePreset = {
  id: AppearancePresetId;
  name: string;
  description: string;
  accent: string;
  iconAccent: string;
  /** Preview swatches: app / sidebar / chat / raised */
  swatches: [string, string, string, string];
  mode: "dark" | "light";
  radius?: number;
  density?: "compact" | "comfortable" | "spacious";
};

/**
 * The essential themes people actually pick: the signature look, a deeper
 * blue, a neutral grey, true black and light. (Earlier rounds shipped 16;
 * a stored id that is no longer listed keeps its saved surfaces and simply
 * shows as a custom theme.)
 *
 * Curated coherent palettes — each changes a full family, not a single hue.
 */
export const APPEARANCE_PRESETS: AppearancePreset[] = [
  {
    id: "signature-dark",
    name: "Zarzour Soft Indigo",
    description:
      "Zarzour's own palette — one indigo family from the logo for panels, text and accent, with teal for anything live.",
    accent: ZARZOUR_UNIFIED_DARK.accentDefault,
    iconAccent: ZARZOUR_UNIFIED_DARK.accentDefault,
    swatches: [
      ZARZOUR_UNIFIED_DARK.bgApp,
      ZARZOUR_UNIFIED_DARK.bgSidebar,
      ZARZOUR_UNIFIED_DARK.bgChat,
      ZARZOUR_UNIFIED_DARK.bgRaised,
    ],
    mode: "dark",
    density: "comfortable",
  },
  {
    id: "midnight",
    name: "Midnight",
    description: "Deeper navy base with cool blue accent.",
    accent: "#6B9CFF",
    iconAccent: "#8BB0FF",
    swatches: ["#0B0F1A", "#12182A", "#182033", "#222B40"],
    mode: "dark",
  },
  {
    id: "graphite",
    name: "Graphite",
    description: "Neutral cool grey workspace for long sessions.",
    accent: "#9AA4B8",
    iconAccent: "#B7C0D1",
    swatches: ["#121418", "#1A1D22", "#22262C", "#2C3138"],
    mode: "dark",
  },
  {
    id: "amoled",
    name: "AMOLED",
    description: "True black surfaces for OLED displays.",
    accent: ZARZOUR_UNIFIED_DARK.accentDefault,
    iconAccent: "#8E99FF",
    swatches: ["#000000", "#0A0A0A", "#121212", "#1A1A1A"],
    mode: "dark",
  },
  {
    id: "daylight",
    name: "Daylight",
    description:
      "Clean light surfaces with deep ink text and a cool indigo accent.",
    accent: "#5064BA",
    iconAccent: "#5064BA",
    swatches: ["#F4F6FA", "#EFF2F8", "#FFFFFF", "#FFFFFF"],
    mode: "light",
  },
];

export function getAppearancePreset(
  id: string | undefined,
): AppearancePreset | undefined {
  return APPEARANCE_PRESETS.find((p) => p.id === id);
}
